import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged, Subject, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'ui-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchInputComponent implements OnInit, OnDestroy {
  @Input() public control: FormControl<string | null> = new FormControl<string>(
    '',
  );
  @Input() public label = '';
  @Input() public placeholder = '';
  @Input() public isControlDisabled = false;
  @Input() public isMaterialLabel = true;
  @Input() public searchDisabled = false;
  @Input() public inputId = '';
  @Input() public autocomplete: 'on' | 'off' = 'on';
  /**
   * If true, the clear button will be shown instead of search button if search term is not empty.
   * Default is false.
   */
  @Input() public clearEnabled = false;
  /** Character Counter UI limit.
   *
   * For example, if 50 is inputted, UI will show "0/50 characters"
   *
   * This is not in place of a Validator and should match the `control` input
   * max length validator if provided. */
  @Input() public characterCountLimit: number | undefined;
  @Output() public search: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Emits when the clear button is clicked.
   */
  @Output() public clear: EventEmitter<void> = new EventEmitter<void>();

  private _destroy$ = new Subject<void>();

  constructor(private _cd: ChangeDetectorRef) {}

  public ngOnInit(): void {
    if (this.isControlDisabled) {
      this.control.disable();
    }

    this.control.statusChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => this._cd.markForCheck()),
        takeUntil(this._destroy$),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onSearch(event: MouseEvent | KeyboardEvent): void {
    if (
      this.searchDisabled ||
      (event instanceof KeyboardEvent && event.key !== 'Enter')
    )
      return;
    event.stopPropagation();
    event.preventDefault();

    this.search.emit(this.control.value || '');
  }

  public onClear(event: MouseEvent | KeyboardEvent): void {
    if (
      !this.clearEnabled ||
      (event instanceof KeyboardEvent && event.key !== 'Enter')
    )
      return;
    event.stopPropagation();
    event.preventDefault();

    this.control.patchValue('');
    this.clear.emit();
  }

  public valueChanged(): void {
    this.control.markAsTouched();
    this.control.markAsDirty();
    this.control.updateValueAndValidity();
  }
}
